import { useRef, useState } from "react";
import "./style.scss";
import { useOutsideClick } from "../../../hooks/useOutsideClick";
import classNames from "classnames";
import OliverText from "@builder/components/general/OliverText";
import { useTranslation } from "react-i18next";
import { formatTestId } from "@builder/utils/format";

interface SelectInputProps {
    title?: string;
    value?: string | number;
    layout?: "row" | "column";
    className?: string;
    options: Record<string | number, string>;
    callback: (value: string) => void;
    noTranslate?: boolean;
    disabled?: boolean;
    placeholder?: string;
    onClick?: () => void;
    inputExtraClass?: string;
    titleExtraClass?: string;
    dropdownDirection?: "up" | "down";
}

const SelectInput: React.FC<SelectInputProps> = ({ title, value, layout = "row", className, options, callback, noTranslate, disabled, onClick, placeholder, inputExtraClass, titleExtraClass, dropdownDirection = "down" }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const selectRef = useRef<HTMLInputElement>(null);

    useOutsideClick(selectRef, () => {
        setIsOpen(false);
    });

    const selectOption = (e: React.MouseEvent) => {
        const value = e.currentTarget.getAttribute("data-value");
        if (typeof value === "string") {
            callback(value);
            setIsOpen(false);
        }

        e.stopPropagation();
    };

    const selectWrapperClass = classNames("select-input-wrapper", {
        ["layout-row"]: layout === "row",
        ["layout-column"]: layout === "column",
        [`${className}`]: className
    });

    const selectWrapperDivClass = classNames("select-wrapper", {
        ["select-active"]: isOpen,
        ["dropdown-up"]: dropdownDirection === "up"
    });

    return (
        <div className={selectWrapperClass} onClick={onClick}>
            {title && <OliverText text={t("generalComponents.selectInput." + title)} size="xs" className={titleExtraClass} />}
            <div className={selectWrapperDivClass} ref={selectRef}>
                <img src="/angled-arrow-down.svg" />
                <input
                    type="text"
                    placeholder={placeholder}
                    className={inputExtraClass}
                    data-testid={formatTestId("select-input", title || "")}
                    value={(typeof value === "string" && (noTranslate ? options[value] : t("generalComponents.selectInput." + options[value]))) || ""}
                    readOnly
                    onClick={() => setIsOpen(!isOpen)}
                    disabled={disabled}
                />
                {isOpen && (
                    <div className="select-input-options">
                        {Object.entries(options).map(([value, label], index) => (
                            <div className="select-input-option" key={index} data-value={value} onClick={selectOption}>
                                {noTranslate ? label : t("generalComponents.selectInput." + label)}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default SelectInput;
